import { useCallback, useLayoutEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Child, getChild } from '../common/child'
import { getCategorySpecifyMasters, getHasUsagePermission, SpecifyMasterType  } from '../common/category'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { Flag, yesNo } from '../common/constant/classification'
import { memberUrl } from '../common/constant/appUrl'
import { tabValue } from '../member/memberService'
import { translate } from '../../i18n'
import { toApiYmd } from '../../utils/dateUtil'
import { undefinedPropsToNull } from '../../utils/objectUtil'
import { GetCategoryMstDto, SpecifyMaster } from '../../dataAccess/webApi/dto/categoryMstDto'
import { GetChildrenVaccinationDto } from '../../dataAccess/webApi/dto/childrenVaccinationDto'
import {
  executeGetChildrenVaccination,
  executePutChildrenVaccination
} from '../../dataAccess/webApi/dao/childrenVaccinationDao'

/** カテゴリーキー */
const CATEGORY_KEY = 'vaccination'
/** 存在フラグ名 */
const EXIST_FLAG_NAME = 'vaccinationFlag'
/** 市民用事業ID（市民用の項目は事業設ID'0'） */
const DEFAULT_PROJECT_ID = '0'
/** 市民用施設ID（市民用のデータは施設ID'0'で作成する） */
const DEFAULT_FACILITY_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** 入力フォームの項目の定義 */
export interface Inputs {
  vaccinationFlag: string | null
  vaccination?: VaccinationForm[]
}

/** 予防接種情報入力フォームの項目の定義 */
export interface VaccinationForm {
  vaccinationId: number
  flag: string
  count?: string | null
  vaccinationDate?: Date | null
  note?: string | null
}

/** 入力フォームの初期値 */
const FORM_DEFAULT_VALUES: Inputs = {
  vaccinationFlag: Flag.OFF,
  vaccination: []
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  // お子さま詳細更新日時
  const [detailUpdateDatetime, setDetailUpdateDatetime] = useState<string>()
  // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // 利用許可
  const [hasUsagePermission, setHasUsagePermission] = useState<boolean>(false)  
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: FORM_DEFAULT_VALUES
  })
  
  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }]
    })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            child,
            hasUsagePermission,
            vaccinationResp
          ] = await Promise.all([
            // カテゴリーマスタ取得
            getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: CATEGORY_KEY, masterType: SpecifyMasterType.VACCINATION}),
            // お子さま情報取得
            getChild(childId, DEFAULT_FACILITY_ID),
            // 利用許可
            getHasUsagePermission(childId),
            // 予防接種情報取得
            executeGetChildrenVaccination(childId, DEFAULT_FACILITY_ID)
          ])

          // カテゴリーマスタ設定
          setCategoryMst(category)
          // お子さま情報設定
          setDetailUpdateDatetime(child.detailUpdateDatetime ?? '')
          // 利用許可 
          setHasUsagePermission(hasUsagePermission)
          // 入力フォームに設定
          const vaccination = vaccinationResp.result
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child, vaccination, category),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // 登録する予防接種情報を設定
            const regVaccinationData = getRegistVaccination(data)
            // 登録情報設定
            const regData = {
              facilityId: DEFAULT_FACILITY_ID,
              detailUpdateDatetime: detailUpdateDatetime ?? '',
              vaccinationFlag: data.vaccinationFlag ?? Flag.OFF,
              vaccination: regVaccinationData ?? [],
            }
            // 登録実行
            const resp = await executePutChildrenVaccination(childId, regData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyChildVaccination.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog, detailUpdateDatetime]
  )

  // 情報返却
  return {
    CATEGORY_KEY,
    EXIST_FLAG_NAME,
    formMethods,
    categoryMst,
    hasUsagePermission,
    onSubmit,
    childrenResultCode,
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (
  child: Child,
  childrenVaccination: GetChildrenVaccinationDto[],
  categoryMst: GetCategoryMstDto[]
) => {
  const inputs = {
    vaccinationFlag: child.vaccinationFlag ?? Flag.OFF,
    vaccination: getVaccinationFormInputs(childrenVaccination, categoryMst)
  }

  return inputs
}

/**
 * お子さま情報から予防接種情報項目の初期値を設定
 * @param user お子さま情報
 * @param categoryMst カテゴリー情報
 * @returns 予防接種情報項目の入力フォームの初期値
 */
const getVaccinationFormInputs = (
  childrenVaccination: GetChildrenVaccinationDto[],
  categoryMst: GetCategoryMstDto[]
)=> {
  const targetCategory = categoryMst.find((value) => value.category === CATEGORY_KEY)
  if (targetCategory?.masterData == null) {
    return undefined
  }

  const vaccinationForm: VaccinationForm[] = []
  targetCategory.masterData.forEach((opt: SpecifyMaster) => {
    let matchedVaccination
    if (childrenVaccination) {
      matchedVaccination = childrenVaccination.find((v) => v.vaccinationId === opt.id)
    }
    
    if (matchedVaccination == null) {
      vaccinationForm.push({
        vaccinationId: opt.id,
        flag: Flag.OFF,
      })
    } else {
    vaccinationForm.push({
      vaccinationId: opt.id,
      flag: Flag.ON,
      count: matchedVaccination.count ?? undefined,
      vaccinationDate: matchedVaccination.vaccinationDate ? new Date(matchedVaccination.vaccinationDate) : undefined,
      note: matchedVaccination.note ?? undefined
    })
    }
  })
  
  return vaccinationForm
}

/**
 * お子さま情報登録APIのINPUT用の予防接種情報を取得
 * @param data 入力値
 * @returns お子さま情報登録APIのINPUT用の予防接種情報
 */
const getRegistVaccination = (data: Inputs) => {
  if (data.vaccinationFlag !== Flag.ON) {
    return undefined
  }
  
  let result: 
  {
    vaccinationId: number
    count: string | null
    vaccinationDate: string | null
    note: string | null
  }[] = []
  
  if (data.vaccination != null) {
    result = data.vaccination
      .filter((value) => value.flag === Flag.ON)
      .map((value) => {
        return undefinedPropsToNull({
          vaccinationId: value.vaccinationId,
          count: value.count ?? null,
          vaccinationDate: value.vaccinationDate ? toApiYmd(value.vaccinationDate) : null,
          note: value.note ?? null,
        })
      })
  }
  
  if (result.length === 0) {
    return undefined
  }
  
  return result
}